import { KeyboardEvent, useRef } from "react";
import type { TabId, TabInfo } from "./mappers";
import { C } from "./theme";

export const tabId = (prefix: string, id: TabId) => `${prefix}-tab-${id}`;
export const panelId = (prefix: string, id: TabId) => `${prefix}-panel-${id}`;

/** Vertical WAI-ARIA tablist: roving tabindex, ↑/↓ (and ←/→), Home/End. */
export const HdcTabList = ({
  tabs,
  active,
  onChange,
  idPrefix,
}: {
  tabs: TabInfo[];
  active: TabId;
  onChange: (id: TabId) => void;
  idPrefix: string;
}) => {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  const focusAt = (i: number) => {
    const n = tabs.length;
    const idx = ((i % n) + n) % n;
    onChange(tabs[idx].id);
    refs.current[idx]?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    switch (e.key) {
      case "ArrowDown":
      case "ArrowRight":
        focusAt(i + 1);
        break;
      case "ArrowUp":
      case "ArrowLeft":
        focusAt(i - 1);
        break;
      case "Home":
        focusAt(0);
        break;
      case "End":
        focusAt(tabs.length - 1);
        break;
      default:
        return;
    }
    e.preventDefault();
  };

  return (
    <div
      role="tablist"
      aria-label="Secciones del reporte"
      aria-orientation="vertical"
      style={{
        flex: "1 1 220px",
        maxWidth: 300,
        alignSelf: "flex-start",
        background: C.nav,
        borderRadius: "0 0 8px 0",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {tabs.map((t, i) => {
        const selected = t.id === active;
        return (
          <button
            key={t.id}
            ref={(el) => (refs.current[i] = el)}
            type="button"
            role="tab"
            id={tabId(idPrefix, t.id)}
            aria-selected={selected}
            aria-controls={panelId(idPrefix, t.id)}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(t.id)}
            onKeyDown={(e) => onKeyDown(e, i)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              width: "100%",
              minHeight: 48,
              padding: "12px 20px 12px 28px",
              margin: 0,
              border: "none",
              borderBottom: `1px solid ${C.line}`,
              boxShadow: selected ? `inset 3px 0 0 ${C.ok}` : undefined,
              background: selected ? C.activeTab : "transparent",
              color: selected ? C.okSoft : C.white,
              fontFamily: "inherit",
              fontWeight: 700,
              fontSize: 13,
              letterSpacing: "0.02em",
              textAlign: "left",
              textTransform: "uppercase",
              cursor: "pointer",
            }}
          >
            <span>{t.label}</span>
            {t.badge !== null && (
              <span
                aria-label={`${t.badge} registros${t.warn ? ", con mora" : ""}`}
                style={{
                  minWidth: 20,
                  padding: "1px 7px",
                  borderRadius: 999,
                  background: t.warn ? C.warn : "rgba(255,255,255,0.14)",
                  color: t.warn ? C.onWarn : C.white,
                  fontSize: 11,
                  textAlign: "center",
                }}
              >
                {t.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};
